import axios from 'axios';
import API_ENDPOINTS from '../components/config/apiConfig';
import { getFirebaseToken } from '../utils/auth';

/**
 * Service for managing service categories and subcategories
 */

// Helper to get Firebase auth token
async function getAuthToken() {
  return await getFirebaseToken();
}

// Cache for categories to avoid repeated API calls
let categoriesCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get all active categories with subcategories
 * @returns {Promise<Array>} Array of category objects with subcategories
 */
export const getAllCategories = async () => {
  try {
    // Check if cache is valid
    if (categoriesCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      return categoriesCache;
    }

    const token = await getAuthToken();
    
    if (!token) {
      console.error('No auth token available.');
      return [];
    }
    
    const response = await axios.get(API_ENDPOINTS.GET_ALL_CATEGORIES, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    // Check response structure
    if (!response.data) {
      return [];
    }
    
    if (response.data.responseCode !== 200 && response.data.responseCode !== '200') {
      console.error('API error:', response.data.responseMessage);
      return [];
    }
    
    // Extract data from response structure: responseData is nested array [[...]]
    let categories = [];
    if (response.data && response.data.responseData && Array.isArray(response.data.responseData)) {
      // Handle nested array structure - responseData[0] contains the actual array
      if (Array.isArray(response.data.responseData[0])) {
        categories = response.data.responseData[0];
      } else {
        categories = response.data.responseData;
      }
    }
    
    // Update cache
    categoriesCache = categories;
    cacheTimestamp = Date.now();
    
    return categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};

/**
 * Get category by ID
 * @param {number} categoryId - The category ID
 * @returns {Promise<Object>} Category object with subcategories
 */
export const getCategoryById = async (categoryId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(API_ENDPOINTS.GET_CATEGORY_BY_ID(categoryId), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    // Extract data from response structure
    if (response.data && response.data.responseData) {
      return response.data.responseData;
    }
    return response.data;
  } catch (error) {
    console.error(`Error fetching category ${categoryId}:`, error);
    throw new Error('Failed to fetch category details.');
  }
};

/**
 * Get subcategories by category ID
 * @param {number} categoryId - The category ID
 * @returns {Promise<Array>} Array of subcategory objects
 */
export const getSubcategoriesByCategoryId = async (categoryId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(API_ENDPOINTS.GET_SUBCATEGORIES_BY_CATEGORY_ID(categoryId), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    // Extract data from response structure
    if (response.data && response.data.responseData) {
      return response.data.responseData;
    }
    return response.data;
  } catch (error) {
    console.error(`Error fetching subcategories for category ${categoryId}:`, error);
    throw new Error('Failed to fetch subcategories.');
  }
};

/**
 * Check if a string is a URL
 * @param {string} str - String to check
 * @returns {boolean} True if URL, false otherwise
 */
export const isIconUrl = (icon) => {
  if (!icon || typeof icon !== 'string') return false;
  return icon.startsWith('http://') || icon.startsWith('https://') || icon.startsWith('//');
};

/**
 * Transform categories for dropdown select
 * @param {Array} categories - Array of category objects
 * @returns {Array} Array of options for select dropdown
 */
export const formatCategoriesForDropdown = (categories) => {
  if (!categories || !Array.isArray(categories)) {
    return [];
  }

  return categories.map(category => ({
    value: category.name,
    label: category.name,
    icon: category.icon,
    id: category.id
  }));
};

/**
 * Transform categories with subcategories for grouped select
 * @param {Array} categories - Array of category objects with subcategories
 * @returns {Object} Object with categories as keys and subcategories as values
 */
export const formatCategoriesWithSubcategories = (categories) => {
  if (!categories || !Array.isArray(categories)) {
    return {};
  }

  const formatted = {};
  
  categories.forEach(category => {
    // API uses 'subCategories' (capital C) instead of 'subcategories'
    if (category.subCategories && Array.isArray(category.subCategories)) {
      formatted[category.name] = category.subCategories.map(sub => sub.name);
    } else {
      formatted[category.name] = [];
    }
  });
  
  return formatted;
};

/**
 * Get main category options (without subcategories)
 * @param {Array} categories - Array of category objects
 * @returns {Array} Array of category names
 */
export const getMainCategoryOptions = (categories) => {
  if (!categories || !Array.isArray(categories)) {
    return [];
  }

  return categories.map(category => category.name);
};

/**
 * Clear categories cache (useful when categories are updated)
 */
export const clearCategoriesCache = () => {
  categoriesCache = null;
  cacheTimestamp = null;
  console.log('Categories cache cleared');
};
